export enum ComplaintStatus {
    PENDING_DSP_ACTION = 'PENDING_DSP_ACTION',
    APPEALING = 'APPEALING',
    REVOKED = 'REVOKED',
    ENFORCED = 'ENFORCED',
    ACKNOWLEDGED = 'ACKNOWLEDGED',
    TIMEOUT = 'TIMEOUT',
    CLOSED = 'CLOSED',
}

export enum CustomerComplaintTab {
    PENDING = 'PENDING',
    APPEALING = 'APPEALING',
}

export const TAB_TO_STATUS_LIST: Record<CustomerComplaintTab, string[]> = {
    [CustomerComplaintTab.PENDING]: [ComplaintStatus.PENDING_DSP_ACTION],
    [CustomerComplaintTab.APPEALING]: [ComplaintStatus.APPEALING],
};

/**
 * DSP 端问题件页面（列表 + 历史）一期仅展示「DNR（签收未收到）」。
 * 列表页/历史页的 ComplaintFilterBar 都引用此常量，保持类型选项一致。
 */
export const DSP_VISIBLE_COMPLAINT_TYPES: string[] = ['DELIVERED_NOT_RECEIVED'];

export interface CustomerComplaint {
    id: number;
    complaintCategory: string;
    complaintType: string;
    /** 客诉原因结构化解析（含原文+三语译文）；前端按 locale 取译文、可切原文；富文本经净化展示 */
    complaintReason?: AppealReasonParsed | null;
    /** 客诉发起渠道：EMAIL/PHONE/OTHER */
    source?: string;
    externalRefId?: string;
    externalRefCode?: string;
    driverId: number | null;
    dspId: number | null;
    orderId?: number;
    hubId?: number;
    warehouseId?: number;
    evidenceFilesJson?: string;
    status: string;
    isFinished: number;
    appealDeadlineAt?: number;
    finishedAt?: number;
    createdAt?: number;
    updatedAt?: number;
    /** 最后操作人姓名；updatedBy 为 0(系统)/账号查不到时为 null，前端兜底为「系统」 */
    operatorName?: string | null;
    /** DSP 提交申诉时间（epoch seconds），未申诉为 null/undefined */
    appealSubmittedAt?: number;
    /** 关联运单号（冗余存储，无关联订单时为 null） */
    trackingNumber?: string;
    /** 关联订单的当前 overallStatus；order 已删除或查询失败时为 null */
    orderOverallStatus?: string;
}

/**
 * DSP 申诉理由结构化解析结果（后端按 i18n.language 选译文，前端无需再 JSON.parse）。
 * 与 ReviewCommentParsed 同模式；translations 为空时按 text 兜底展示。
 */
export interface AppealReasonParsed {
    text: string;
    lang: string;
    translations: Array<{lang: string; text: string}>;
}

export interface ComplaintAppealDetail {
    id: number;
    complaintId: number;
    reviewTaskId?: number;
    /** 后端结构化解析的申诉理由；前端按 i18n.language 选 translations[locale] ?? text */
    appealReason?: AppealReasonParsed | null;
    /** 申诉附件预签名 URL 列表，由后端按 s3 keys 拼装；前端直接展示 */
    appealAttachmentS3UrlsJson?: string[];
    appealSubmittedAt?: number;
    appealSubmittedBy?: number;
    reviewResult?: string;
    reviewerId?: number;
    /** 后端结构化解析的审核评论，前端无需自行 JSON.parse；入库 JSON 为空/解析失败时为 null */
    reviewComment?: ReviewCommentParsed | null;
    reviewedAt?: number;
}

export interface ReviewCommentTranslationEntry {
    /** BCP-47 short code：zh|en|es */
    lang: string;
    /** 该语言下的翻译文本 */
    text: string;
}

export interface ReviewRejectReasonOptionInline {
    /** 字典 code，与 review_comment_json.tagKeys 中的值一致 */
    code: string;
    /** 展示顺序 */
    displayOrder?: number;
    /** 可扩展多语言文案，按 locale 取 */
    texts: {lang: string; text: string}[];
}

export interface ReviewCommentParsed {
    /** 标签 code 列表（与 ReviewRejectReasonOption.code 对齐） */
    tagKeys: string[];
    /** 按 tagKeys 顺序解析后的字典项；前端展示直接按 locale 从 texts 取 */
    tagOptions: ReviewRejectReasonOptionInline[];
    /** 自由文本原文 */
    text: string;
    /** 自由文本源语言 */
    lang: string;
    /** 自由文本翻译列表 */
    translations: ReviewCommentTranslationEntry[];
}
